// ambient.js — hero background engine (ported from website2-ambient.js), five moods:
//   both   — paw trails underneath, bath bubbles floating over them (default)
//   suds   — canvas-2D bath bubbles drifting up with iridescent rims
//   paws   — an invisible pup wanders across: paw-print trails fade in and out
//   aurora — WebGL domain-warped pastel field (software-GL bails to CSS)
//   calm   — no canvas; the CSS gradient underneath shows
// Shared discipline: 30fps cap, DPR-aware, pauses offscreen, reduced-motion = one still frame.
const LAV = [124, 107, 217]
const BLUSH = [221, 102, 166]
const SKY = [91, 121, 227]
const rgba = (c, a) => 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'
const rnd = (a, b) => a + Math.random() * (b - a)

/* ── shared 2D-canvas loop harness ── */
function loop2d(canvas, { init, step, still }) {
	const ctx = canvas.getContext('2d')
	if (!ctx) { canvas.classList.add('n2-ambient-off'); return null }
	const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	let raf = 0, visible = true, running = true, last = 0, W = 0, H = 0, dpr = 1, ready = false
	const state = {}
	function size() {
		dpr = Math.min(window.devicePixelRatio || 1, 2)
		W = canvas.clientWidth; H = canvas.clientHeight
		const w = Math.round(W * dpr), h = Math.round(H * dpr)
		const changed = canvas.width !== w || canvas.height !== h
		if (changed) { canvas.width = w; canvas.height = h }
		if (changed || !ready) { ctx.setTransform(dpr, 0, 0, dpr, 0, 0); init(state, W, H); ready = true }
	}
	function frame(now) {
		raf = 0
		if (now - last >= 33) { last = now; size(); step(state, ctx, W, H, now / 1000) }
		if (!reduce && visible && running) raf = requestAnimationFrame(frame)
	}
	const kick = () => { if (!raf && running) raf = requestAnimationFrame(frame) }
	if (reduce) { size(); still ? still(state, ctx, W, H) : step(state, ctx, W, H, 137); return { stop() {} } }
	const io = new IntersectionObserver((es) => { visible = es[0].isIntersecting; if (visible) kick() })
	io.observe(canvas)
	const ro = new ResizeObserver(kick)
	ro.observe(canvas)
	kick()
	return { stop() { running = false; if (raf) cancelAnimationFrame(raf); io.disconnect(); ro.disconnect() } }
}

/* ── compose 2D layers ({init, draw, still?}) into one loop: one clear, draw in order ── */
function run2d(canvas, layers) {
	return loop2d(canvas, {
		init(st, W, H) { st.sub = layers.map((l) => { const s = {}; l.init(s, W, H); return s }) },
		step(st, ctx, W, H, t) { ctx.clearRect(0, 0, W, H); layers.forEach((l, i) => l.draw(st.sub[i], ctx, W, H, t)) },
		still(st, ctx, W, H) { ctx.clearRect(0, 0, W, H); layers.forEach((l, i) => (l.still || l.draw)(st.sub[i], ctx, W, H, 137)) }
	})
}

/* ── suds — bath bubbles ── */
function sudsLayer() {
	const TINTS = [LAV, LAV, SKY, BLUSH]
	function mkBubble(W, H, top) {
		const r = rnd(7, 46)
		return { x: rnd(0, W), y: top ? -r - rnd(0, 40) : rnd(0, H), r, tint: TINTS[(Math.random() * TINTS.length) | 0],
			v: rnd(9, 22) * (r / 30 + 0.5), sway: rnd(10, 26), ph: rnd(0, 6.28), a: rnd(0.05, 0.13) }
	}
	return {
		init(st, W, H) {
			st.bubbles = Array.from({ length: Math.min(30, Math.round(W / 46)) }, () => mkBubble(W, H, false))
			st.t0 = null
		},
		draw(st, ctx, W, H, t) {
			if (st.t0 == null) st.t0 = t
			const dt = Math.min(0.08, t - st.t0)
			st.t0 = t
			for (const b of st.bubbles) {
				b.y -= b.v * dt
				if (b.y < -b.r - 50) Object.assign(b, mkBubble(W, H, true), { y: H + b.r + rnd(0, 30) })
				const x = b.x + Math.sin(t * 0.5 + b.ph) * b.sway
				const fade = Math.min(1, (H - b.y) / 120, (b.y + b.r + 60) / 160)
				const a = b.a * Math.max(0, fade)
				if (a <= 0.004) continue
				// body — barely-there fill
				const g = ctx.createRadialGradient(x - b.r * 0.3, b.y - b.r * 0.35, b.r * 0.15, x, b.y, b.r)
				g.addColorStop(0, rgba(b.tint, a * 0.16))
				g.addColorStop(0.78, rgba(b.tint, a * 0.34))
				g.addColorStop(1, rgba(b.tint, a))
				ctx.fillStyle = g
				ctx.beginPath(); ctx.arc(x, b.y, b.r, 0, 6.2832); ctx.fill()
				// iridescent rim: two tinted arcs
				ctx.lineWidth = Math.max(1, b.r * 0.05)
				ctx.strokeStyle = rgba(BLUSH, a * 1.15)
				ctx.beginPath(); ctx.arc(x, b.y, b.r * 0.97, 0.6 + b.ph, 2.1 + b.ph); ctx.stroke()
				ctx.strokeStyle = rgba(SKY, a * 1.05)
				ctx.beginPath(); ctx.arc(x, b.y, b.r * 0.97, 3.3 + b.ph, 4.6 + b.ph); ctx.stroke()
				// highlight
				ctx.fillStyle = 'rgba(255,255,255,' + (a * 2.2) + ')'
				ctx.beginPath(); ctx.ellipse(x - b.r * 0.38, b.y - b.r * 0.42, b.r * 0.2, b.r * 0.11, -0.7, 0, 6.2832); ctx.fill()
			}
		}
	}
}

/* ── paws — wandering paw-print trails ── */
function drawPaw(ctx, x, y, ang, s, a, tint) {
	ctx.save(); ctx.translate(x, y); ctx.rotate(ang); ctx.fillStyle = rgba(tint, a)
	ctx.beginPath(); ctx.ellipse(0, s * 0.32, s * 0.5, s * 0.4, 0, 0, 6.2832); ctx.fill()
	const toes = [[-s * 0.52, -s * 0.18, -0.5], [-s * 0.18, -s * 0.5, -0.15], [s * 0.18, -s * 0.5, 0.15], [s * 0.52, -s * 0.18, 0.5]]
	for (const [tx, ty, tr] of toes) {
		ctx.beginPath(); ctx.ellipse(tx, ty, s * 0.16, s * 0.22, tr, 0, 6.2832); ctx.fill()
	}
	ctx.restore()
}
function pawsLayer() {
	function mkTrail(W, H, t) {
		const edge = Math.random()
		let x, y, ang
		if (edge < 0.38) { x = -30; y = rnd(H * 0.15, H * 0.8); ang = rnd(-0.5, 0.5) }
		else if (edge < 0.76) { x = W + 30; y = rnd(H * 0.15, H * 0.8); ang = 3.1416 + rnd(-0.5, 0.5) }
		else { x = rnd(W * 0.1, W * 0.9); y = H + 30; ang = -1.5708 + rnd(-0.45, 0.45) }
		return { x, y, ang, born: t, n: 8 + ((Math.random() * 4) | 0), stepLen: rnd(34, 44), s: rnd(10, 13),
			period: 0.46, life: 3.4, tint: Math.random() < 0.72 ? LAV : BLUSH, drift: rnd(-0.16, 0.16) }
	}
	return {
		init(st, W, H) { st.trails = []; st.next = null },
		draw(st, ctx, W, H, t) {
			if (st.next === null) { st.trails.push(Object.assign(mkTrail(W, H, t), { born: t - 1.7 })); st.next = t + rnd(1.6, 2.6) }
			if (t >= st.next && st.trails.length < 2) { st.trails.push(mkTrail(W, H, t)); st.next = t + rnd(3.6, 5.4) }
			st.trails = st.trails.filter((tr) => {
				const age = t - tr.born
				const shown = Math.min(tr.n, Math.floor(age / tr.period) + 1)
				let alive = false
				for (let i = 0; i < shown; i++) {
					const pa = age - i * tr.period // this print's age
					const a = pa < 0.35 ? pa / 0.35 : Math.max(0, 1 - (pa - tr.life) / 0.9)
					if (a <= 0) continue
					alive = true
					const ang = tr.ang + tr.drift * i
					const px = tr.x + Math.cos(ang) * tr.stepLen * i, py = tr.y + Math.sin(ang) * tr.stepLen * i
					const side = (i % 2 ? 1 : -1) * tr.s * 0.75
					drawPaw(ctx, px + Math.cos(ang + 1.5708) * side, py + Math.sin(ang + 1.5708) * side,
						ang + 1.5708 + (i % 2 ? 0.08 : -0.08), tr.s, Math.min(0.14, a * 0.14), tr.tint)
				}
				return alive || age < tr.n * tr.period
			})
		},
		still(st, ctx, W, H) {
			for (let i = 0; i < 7; i++) drawPaw(ctx, rnd(W * 0.05, W * 0.95), rnd(H * 0.1, H * 0.9), rnd(-0.6, 0.6), 11, 0.07, i % 3 ? LAV : BLUSH)
		}
	}
}

/* ── aurora — the WebGL field ── */
const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'
const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 u_res; uniform float u_t;
float h(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(h(i),h(i+vec2(1.,0.)),f.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.,1.)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.55;for(int i=0;i<3;i++){v+=a*n(p);p=p*2.04+13.7;a*=.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/u_res; vec2 asp=vec2(u_res.x/u_res.y,1.);
  vec2 q=uv*asp*1.35; float t=u_t*.018;
  vec2 warp=vec2(fbm(q+vec2(t,-t*.7)),fbm(q+vec2(-t*.6,t)+5.2));
  float f=fbm(q+1.6*warp+vec2(t*.4,0.));
  vec3 base=vec3(.992,.988,1.);
  vec3 lav =vec3(.486,.42,.851);
  vec3 blush=vec3(.867,.4,.651);
  vec3 sky =vec3(.357,.475,.89);
  vec3 col=base;
  col=mix(col,lav,  smoothstep(.32,.82,f)*.17);
  col=mix(col,blush,smoothstep(.55,.95,warp.x)*.10);
  col=mix(col,sky,  smoothstep(.55,.95,warp.y)*.07);
  float d=distance(uv,vec2(.5,.42));
  col=mix(col,base,smoothstep(.28,.75,d));
  gl_FragColor=vec4(col,1.);
}`
function aurora(canvas) {
	let gl
	try { gl = canvas.getContext('webgl', { antialias: false, depth: false, stencil: false, alpha: false, powerPreference: 'low-power', preserveDrawingBuffer: true, failIfMajorPerformanceCaveat: false }) } catch (e) { gl = null }
	if (!gl) { canvas.classList.add('n2-ambient-off'); return null }
	let software = false
	try {
		const dbg = gl.getExtension('WEBGL_debug_renderer_info')
		const r = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : ''
		software = /swiftshader|llvmpipe|software|basic render/i.test(r)
	} catch (e) {}
	if (software) { canvas.classList.add('n2-ambient-off'); return null }
	const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return gl.getShaderParameter(o, gl.COMPILE_STATUS) ? o : null }
	const vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG)
	if (!vs || !fs) { canvas.classList.add('n2-ambient-off'); return null }
	const pr = gl.createProgram()
	gl.attachShader(pr, vs); gl.attachShader(pr, fs); gl.linkProgram(pr); gl.useProgram(pr)
	const buf = gl.createBuffer()
	gl.bindBuffer(gl.ARRAY_BUFFER, buf)
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
	const loc = gl.getAttribLocation(pr, 'p')
	gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
	const uRes = gl.getUniformLocation(pr, 'u_res'), uT = gl.getUniformLocation(pr, 'u_t')
	const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	let raf = 0, visible = true, running = true, last = 0
	const t0 = performance.now()
	function size() {
		const s = Math.min(0.45 * (window.devicePixelRatio || 1), 0.9)
		const w = Math.min(860, Math.max(2, Math.round(canvas.clientWidth * s)))
		const hh = Math.max(2, Math.round(canvas.clientHeight * s))
		if (canvas.width !== w || canvas.height !== hh) { canvas.width = w; canvas.height = hh; gl.viewport(0, 0, w, hh) }
	}
	function draw(now) {
		size()
		gl.uniform2f(uRes, canvas.width, canvas.height)
		gl.uniform1f(uT, reduce ? 137 : (now - t0) / 1000)
		gl.drawArrays(gl.TRIANGLES, 0, 3)
		if (!draw.checked) {
			draw.checked = true
			try {
				const px = new Uint8Array(4)
				gl.readPixels(Math.floor(canvas.width / 2), Math.floor(canvas.height / 2), 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px)
				if (px[0] + px[1] + px[2] < 30) { running = false; canvas.classList.add('n2-ambient-off') }
			} catch (e) {}
		}
	}
	function frame(now) {
		raf = 0
		if (now - last >= 33) { last = now; draw(now) }
		if (!reduce && visible && running) raf = requestAnimationFrame(frame)
	}
	const kick = () => { if (!raf && running) raf = requestAnimationFrame(frame) }
	const io = new IntersectionObserver((es) => { visible = es[0].isIntersecting; if (visible) kick() })
	io.observe(canvas)
	const ro = new ResizeObserver(() => { last = 0; kick() })
	ro.observe(canvas)
	kick()
	return { stop() { running = false; if (raf) cancelAnimationFrame(raf); io.disconnect(); ro.disconnect() } }
}

export function n2Ambient(canvas, mode) {
	canvas.classList.remove('n2-ambient-off')
	if (mode === 'calm') { canvas.classList.add('n2-ambient-off'); return { stop() {} } }
	if (mode === 'aurora') return aurora(canvas)
	if (mode === 'paws') return run2d(canvas, [pawsLayer()])
	if (mode === 'suds') return run2d(canvas, [sudsLayer()])
	return run2d(canvas, [pawsLayer(), sudsLayer()]) // 'both' — paws on the floor, bubbles float above
}
